/**
 * ReadinessGapCard — dashboard mini-card that renders the top ≤3
 * professional matches returned by GET /api/marketplace/matches.
 *
 * Silent-fail contract:
 *   - 401 / 403 / network error → render nothing (never block dashboard)
 *   - API returns empty matches + gaps               → hint to complete a proposal quality score
 *   - API returns non-empty matches                  → render the mini-cards
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import { api } from "@/api/client";
import DemoBadge from "./DemoBadge";

const CATEGORY_LABELS = {
  grant_writing: "Grant Writing",
  budget_planning: "Budget Planning",
  cpa_services: "CPA Services",
  nonprofit_formation: "Nonprofit Formation",
  business_formation: "Business Formation",
  credit_repair: "Credit Repair",
  business_plan: "Business Plan",
  sales_marketing: "Sales & Marketing",
};

const GAP_WEAK_SECTION_MAP = {
  grant_writing: "grant proposal narrative",
  budget_planning: "budget narrative",
  cpa_services: "budget narrative",
  nonprofit_formation: "nonprofit structure",
  business_formation: "business entity setup",
  credit_repair: "credit profile",
  business_plan: "business plan",
  sales_marketing: "revenue growth",
};

function labelForSlug(slug) {
  return CATEGORY_LABELS[slug] || slug.replace(/_/g, " ");
}

function reasonForGap(matchedGaps) {
  if (!Array.isArray(matchedGaps) || matchedGaps.length === 0) return null;
  const primary = matchedGaps[0];
  const weak = GAP_WEAK_SECTION_MAP[primary] || labelForSlug(primary).toLowerCase();
  return `Because your ${weak} needs work`;
}

export default function ReadinessGapCard() {
  const [data, setData] = useState(null);
  const [ready, setReady] = useState(false);
  const [silentHide, setSilentHide] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/marketplace/matches")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((err) => {
        // 401/403/network → silently hide the card.
        if (!cancelled) setSilentHide(true);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return null;
  if (silentHide) return null;
  if (!data) return null;

  const matches = data.matches || [];
  const gaps = data.gaps || [];

  // Guidance branch — we have (or don't have) gaps but no matches.
  if (matches.length === 0) {
    // If there are no gaps AND no matches: skip rendering — nothing useful.
    if (gaps.length === 0) return null;
    // Gaps exist but no active-category matches: hint to complete a quality score.
    return (
      <div
        data-testid="readiness-gap-card"
        data-variant="empty-hint"
        className="rounded-2xl p-5 md:p-6"
        style={{
          background: "var(--v2-surface, #13131A)",
          border: "1px solid var(--v2-border, rgba(255,255,255,0.08))",
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-4 w-4" style={{ color: "var(--v2-accent-strong, #5D50C8)" }} aria-hidden="true" />
          <h3
            className="text-lg font-semibold"
            style={{
              color: "var(--v2-text, #F5F5F0)",
              fontFamily: "'Space Grotesk', 'Inter', sans-serif",
            }}
          >
            Professional support
          </h3>
        </div>
        <p className="text-sm" style={{ color: "var(--v2-text-muted, #A1A1AA)" }}>
          Complete a proposal quality score to see personalized professional
          matches for your funding gaps.
        </p>
        <Link
          to="/grants"
          data-testid="readiness-gap-quality-hint-link"
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold"
          style={{ color: "var(--v2-accent-strong, #5D50C8)" }}
        >
          Go to Grant Navigator <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
    );
  }

  const top3 = matches.slice(0, 3);
  // Precompute a URL to the marketplace with the primary matched category
  // filter for the "See all matches" link.
  const primarySlug = gaps[0];
  const seeAllHref = primarySlug ? `/marketplace?category=${encodeURIComponent(primarySlug)}` : "/marketplace";

  return (
    <div
      data-testid="readiness-gap-card"
      data-variant="matches"
      className="rounded-2xl p-5 md:p-6"
      style={{
        background: "var(--v2-surface, #13131A)",
        border: "1px solid var(--v2-border, rgba(255,255,255,0.08))",
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" style={{ color: "var(--v2-accent-strong, #5D50C8)" }} aria-hidden="true" />
          <h3
            className="text-lg font-semibold"
            style={{
              color: "var(--v2-text, #F5F5F0)",
              fontFamily: "'Space Grotesk', 'Inter', sans-serif",
            }}
          >
            Professionals who can help close your funding gaps
          </h3>
        </div>
        <Link
          to={seeAllHref}
          data-testid="readiness-gap-see-all-link"
          className="inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap"
          style={{ color: "var(--v2-accent-strong, #5D50C8)" }}
        >
          See all matches <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {top3.map((m) => {
          const isDemo = Boolean(m.is_demo || m.demo_profile);
          const reason = reasonForGap(m.matched_gap_categories);
          return (
            <Link
              key={m.id}
              to={`/marketplace/${m.id}`}
              data-testid="readiness-gap-match"
              data-is-demo={isDemo ? "true" : "false"}
              className="group rounded-xl p-4 block transition-all hover:-translate-y-0.5"
              style={{
                background: "var(--v2-surface-subtle, rgba(255,255,255,0.02))",
                border: isDemo
                  ? "1px solid rgba(167,139,250,0.42)"
                  : "1px solid var(--v2-border, rgba(255,255,255,0.08))",
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <h4
                  className="text-sm font-semibold leading-snug"
                  style={{
                    color: "var(--v2-text, #F5F5F0)",
                    fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                  }}
                >
                  {m.name}
                </h4>
                {isDemo && <DemoBadge compact />}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(m.matched_gap_categories || m.categories || [])
                  .slice(0, 2)
                  .map((slug) => (
                    <span
                      key={slug}
                      className="text-[10px] uppercase tracking-[0.14em] rounded-full px-2 py-0.5 font-medium"
                      style={{
                        background: "var(--v2-accent-soft, rgba(167,139,250,0.15))",
                        color: "var(--v2-accent-strong, #5D50C8)",
                      }}
                    >
                      {labelForSlug(slug)}
                    </span>
                  ))}
              </div>
              {reason && (
                <div
                  className="mt-2 text-[11px]"
                  style={{ color: "var(--v2-text-muted, #A1A1AA)" }}
                >
                  {reason}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
